import { useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import { getMySettings, patchMySettings, type UserSettingsDto } from '../../../api/users';
import {
  applyCustomTheme, themeFromSettings, isHex, readableTextOn,
  type ButtonRadius, type CustomTheme,
} from '../../../lib/theme-custom';
import {
  BUILT_IN_THEMES, MAX_SAVED_THEMES, THEME_NAME_MAX,
  makeSavedTheme, readLocalThemes, sanitizeSavedThemes, uniqueName, writeLocalThemes,
  type BuiltInTheme, type SavedTheme,
} from '../../../lib/theme-presets';
import { paletteFor } from '../../../lib/theme-catalog';
import { ThemePreview, type PreviewColors } from './ThemePreview';
import { RotateCcw, Check, Loader2, Plus, X } from '../../../icons';
import {
  tSettings, getSettingsLang, subscribeSettingsLang, type SettingsI18nKey,
} from '../../../lib/settings-i18n';
import './ThemePanel.css';

/**
 * 테마 만들기 (2026-08-20) — 환경설정 > 테마 만들기.
 *
 * 시안: https://claude.ai/code/artifact/98659e1c-ef06-4966-b860-5bb6b3d3dd6a
 *
 * 화면 구성(사용자 확정):
 *   ① **미리보기** — 타이틀 위 · 설명 왼쪽 · 6영역 미니 화면 오른쪽. "실제 보기" 를 대신한다.
 *   ② **테마 불러오기** — 기본 제공 12종 + 내가 등록한 테마.
 *   ③ **색 15종** — 배경 · 좌측 레일 · 메뉴 · 우측 레일 · 상태바 · 표 · 표 헤더 · 헤더 글자 ·
 *      아이콘 · 아이콘 호버 · 주색 · 선택 배경 · 라인 · 글자 · 보조 글자.
 *   ④ 버튼 모서리 · 바뀌는 속도(한 줄) → 저장 · 테마 등록 · 되돌리기.
 *
 * ★ 색 고르기는 **나열이 아니라 한 칸**이다 — 지금 색을 보여 주고, 누르면 그 위로 색상값과
 *   "이 테마의 색"(흰·검 포함)이 뜬다. 옆 칸은 커스텀 색 선택기.
 * ★ 고른 값은 즉시 **진짜 화면**에 반영한다(미리보기 상자 안이 아니라). 저장하지 않고 나가면 되돌린다.
 */

interface Draft {
  primary: string | null;
  accent: string | null;
  danger: string | null;
  line: string | null;
  surface: string | null;
  bg: string | null;
  rail: string | null;
  menu: string | null;
  rrail: string | null;
  status: string | null;
  table: string | null;
  tableHead: string | null;
  tableHeadText: string | null;
  icon: string | null;
  iconHover: string | null;
  tint: string | null;
  text: string | null;
  textMuted: string | null;
  buttonRadius: ButtonRadius;
  transitionMs: number;
}

type ColorKey = Exclude<keyof Draft, 'buttonRadius' | 'transitionMs'>;

const EMPTY: Draft = {
  primary: null, accent: null, danger: null, line: null, surface: null,
  bg: null, rail: null, menu: null, rrail: null, status: null,
  table: null, tableHead: null, tableHeadText: null, icon: null, iconHover: null,
  tint: null, text: null, textMuted: null,
  buttonRadius: 'round', transitionMs: 350,
};

/** 색 항목 → 미리보기에서 그 색을 들고 있는 키. 칸에 보여 줄 "지금 색" 을 여기서 가져온다. */
const PREVIEW_OF: Record<ColorKey, keyof PreviewColors> = {
  bg: 'bg', rail: 'rail', menu: 'menu', rrail: 'rrail', status: 'status',
  table: 'table', tableHead: 'thead', tableHeadText: 'theadText',
  icon: 'icon', iconHover: 'iconHover', primary: 'primary', tint: 'tint',
  line: 'line', text: 'text', textMuted: 'muted',
  accent: 'primary', danger: 'primary', surface: 'menu',
};

/** 화면에 보이는 순서 = 이 배열 순서(한 줄에 4개). `token` 은 값이 없을 때 읽을 지금 색. */
const FIELDS: ReadonlyArray<{
  key: ColorKey; labelKey: SettingsI18nKey; descKey: SettingsI18nKey; token: string;
}> = [
  { key: 'bg', labelKey: 'settings.theme.a_bg', descKey: 'settings.theme.a_bg_desc', token: '--bg' },
  { key: 'rail', labelKey: 'settings.theme.a_rail', descKey: 'settings.theme.a_rail_desc', token: '--surface' },
  { key: 'menu', labelKey: 'settings.theme.a_menu', descKey: 'settings.theme.a_menu_desc', token: '--surface' },
  { key: 'rrail', labelKey: 'settings.theme.a_rrail', descKey: 'settings.theme.a_rrail_desc', token: '--surface' },
  { key: 'status', labelKey: 'settings.theme.a_status', descKey: 'settings.theme.a_status_desc', token: '--surface-2' },
  { key: 'table', labelKey: 'settings.theme.a_table', descKey: 'settings.theme.a_table_desc', token: '--bg' },
  { key: 'tableHead', labelKey: 'settings.theme.a_thead', descKey: 'settings.theme.a_thead_desc', token: '--surface-2' },
  { key: 'tableHeadText', labelKey: 'settings.theme.a_thead_text', descKey: 'settings.theme.a_thead_text_desc', token: '--text-muted' },
  { key: 'icon', labelKey: 'settings.theme.a_icon', descKey: 'settings.theme.a_icon_desc', token: '--text-muted' },
  { key: 'iconHover', labelKey: 'settings.theme.a_icon_hover', descKey: 'settings.theme.a_icon_hover_desc', token: '--primary' },
  { key: 'primary', labelKey: 'settings.theme.primary', descKey: 'settings.theme.primary_desc', token: '--primary' },
  { key: 'tint', labelKey: 'settings.theme.a_tint', descKey: 'settings.theme.a_tint_desc', token: '--primary-tint' },
  { key: 'line', labelKey: 'settings.theme.line', descKey: 'settings.theme.line_desc', token: '--border' },
  { key: 'text', labelKey: 'settings.theme.a_text', descKey: 'settings.theme.a_text_desc', token: '--text' },
  { key: 'textMuted', labelKey: 'settings.theme.a_text_muted', descKey: 'settings.theme.a_text_muted_desc', token: '--text-muted' },
];

const RADII: ReadonlyArray<{ key: ButtonRadius; labelKey: SettingsI18nKey }> = [
  { key: 'square', labelKey: 'settings.theme.radius_square' },
  { key: 'round', labelKey: 'settings.theme.radius_round' },
  { key: 'pill', labelKey: 'settings.theme.radius_pill' },
];

const SPEEDS: ReadonlyArray<{ ms: number; labelKey: SettingsI18nKey }> = [
  { ms: 0, labelKey: 'settings.theme.speed_instant' },
  { ms: 350, labelKey: 'settings.theme.speed_smooth' },
  { ms: 900, labelKey: 'settings.theme.speed_slow' },
];

function draftOf(s: UserSettingsDto | null): Draft {
  const t = themeFromSettings(s);
  if (!t) return EMPTY;
  return {
    primary: t.primary ?? null, accent: t.accent ?? null, danger: t.danger ?? null,
    line: t.line ?? null, surface: t.surface ?? null,
    bg: t.bg ?? null, rail: t.rail ?? null, menu: t.menu ?? null, rrail: t.rrail ?? null,
    status: t.status ?? null, table: t.table ?? null, tableHead: t.tableHead ?? null,
    tableHeadText: t.tableHeadText ?? null, icon: t.icon ?? null, iconHover: t.iconHover ?? null,
    tint: t.tint ?? null, text: t.text ?? null, textMuted: t.textMuted ?? null,
    buttonRadius: t.buttonRadius ?? 'round',
    transitionMs: t.transitionMs ?? 350,
  };
}

/** 저장 요청 본문 — 서버 컬럼 이름으로 옮긴다(바뀔 때 한 곳만 고치도록 모아 둔다). */
function patchOf(d: Draft) {
  return {
    themePrimary: d.primary, themeAccent: d.accent, themeDanger: d.danger,
    themeLine: d.line, themeSurface: d.surface,
    themeBg: d.bg, themeRail: d.rail, themeMenu: d.menu, themeRrail: d.rrail,
    themeStatus: d.status, themeTable: d.table, themeTableHead: d.tableHead,
    themeTableHeadText: d.tableHeadText, themeIcon: d.icon, themeIconHover: d.iconHover,
    themeTint: d.tint, themeText: d.text, themeTextMuted: d.textMuted,
    buttonRadius: d.buttonRadius, themeTransitionMs: d.transitionMs,
  };
}

function themeOf(d: Draft): CustomTheme {
  return { ...d };
}

/** 등록한 테마가 지금 화면과 같은지 — 목록에서 "지금 이것" 을 표시하는 데만 쓴다. */
function sameAsDraft(t: SavedTheme, d: Draft): boolean {
  return FIELDS.every((f) => (t[f.key] ?? null) === d[f.key])
    && t.buttonRadius === d.buttonRadius && t.transitionMs === d.transitionMs;
}

/** 기본 제공 테마는 색 3개만 담는다(라인·표면은 모드마다 달라야 해서 비운다 — theme-presets.ts). */
function sameAsBuiltIn(p: BuiltInTheme, d: Draft): boolean {
  return p.primary === d.primary && p.accent === d.accent && p.danger === d.danger;
}

export function ThemePanel() {
  useSyncExternalStore(subscribeSettingsLang, getSettingsLang, getSettingsLang);
  const [saved, setSaved] = useState<Draft>(EMPTY);
  const [draft, setDraft] = useState<Draft>(EMPTY);
  const [busy, setBusy] = useState(false);
  const [savedAt, setSavedAt] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [themes, setThemes] = useState<SavedTheme[]>([]);
  const [store, setStore] = useState<'server' | 'local'>('server');
  const [naming, setNaming] = useState(false);
  const [nameInput, setNameInput] = useState('');
  const [openKey, setOpenKey] = useState<ColorKey | null>(null);


  useEffect(() => {
    let cancelled = false;
    getMySettings()
      .then((s) => {
        if (cancelled) return;
        const d = draftOf(s);
        setSaved(d);
        setDraft(d);

        /* 서버가 키 자체를 안 주면(undefined) 구 서버다 → 기기 저장으로 간다.
           배열을 주는데 비어 있고 기기에만 있으면, 이번 한 번 서버로 올린다(옮겨 담기). */
        const fromServer = s.themePresets;
        if (Array.isArray(fromServer)) {
          const list = sanitizeSavedThemes(fromServer);
          const local = readLocalThemes();
          setStore('server');
          if (list.length === 0 && local.length > 0) {
            setThemes(local);
            patchMySettings({ themePresets: local }).catch(() => { /* 다음 등록 때 다시 시도된다 */ });
          } else {
            setThemes(list);
          }
        } else {
          setStore('local');
          setThemes(readLocalThemes());
        }
      })
      .catch(() => { if (!cancelled) setThemes(readLocalThemes()); });
    return () => { cancelled = true; };
  }, []);

  /* 화면을 떠날 때 저장하지 않은 색은 되돌린다. 저장된 값을 ref 로 들고 **떠날 때 한 번만** —
     `saved` 를 의존성에 넣으면 저장 직후에도 정리가 돌아 방금 저장한 색이 되돌아간다. */
  const savedRef = useRef(saved);
  savedRef.current = saved;
  useEffect(() => () => { applyCustomTheme({ ...themeOf(savedRef.current), transitionMs: 0 }); }, []);

  const dirty = JSON.stringify(draft) !== JSON.stringify(saved);

  const preview = useCallback((next: Draft) => {
    setDraft(next);
    setSavedAt(null);
    applyCustomTheme({ ...next });
  }, []);

  /* 미리보기에 넣을 **실제로 보이는 색**. 안 고른 항목은 지금 화면의 토큰 값을 읽는다 —
     그래야 "고른 것만 바뀐다" 는 규칙이 미리보기에도 그대로 보인다. */
  const previewColors: PreviewColors = useMemo(() => {
    const css = typeof window !== 'undefined' ? getComputedStyle(document.documentElement) : null;
    const tok = (name: string, fallback: string) => (css?.getPropertyValue(name).trim() || fallback);
    const pick = (key: ColorKey, token: string, fallback: string) => draft[key] ?? tok(token, fallback);
    const primary = pick('primary', '--primary', '#3b82f6');
    return {
      bg: pick('bg', '--bg', '#ffffff'),
      rail: pick('rail', '--surface', '#f8fafc'),
      menu: pick('menu', '--surface', '#f8fafc'),
      rrail: pick('rrail', '--surface', '#f8fafc'),
      status: pick('status', '--surface-2', '#f1f5f9'),
      table: pick('table', '--bg', '#ffffff'),
      thead: pick('tableHead', '--surface-2', '#f1f5f9'),
      theadText: pick('tableHeadText', '--text-muted', '#64748b'),
      icon: pick('icon', '--text-muted', '#64748b'),
      iconHover: pick('iconHover', '--primary', primary),
      primary,
      primaryText: isHex(primary) ? readableTextOn(primary) : '#ffffff',
      tint: pick('tint', '--primary-tint', '#dbeafe'),
      line: pick('line', '--border', '#e2e8f0'),
      text: pick('text', '--text', '#1e293b'),
      muted: pick('textMuted', '--text-muted', '#64748b'),
    };
  }, [draft]);

  /** 플로팅 목록에 뜨는 "이 테마의 색" — 지금 화면이 실제로 쓰는 색 + 흰·검. */
  const themeColors = useMemo(() => {
    const list = ['#ffffff', '#000000'];
    for (const v of Object.values(previewColors)) {
      const hex = typeof v === 'string' ? v.toLowerCase() : '';
      if (isHex(hex) && !list.includes(hex)) list.push(hex);
    }
    return list;
  }, [previewColors]);

  async function persistThemes(list: SavedTheme[]): Promise<void> {
    setThemes(list);
    if (store === 'server') {
      try {
        await patchMySettings({ themePresets: list });
        return;
      } catch {
        setStore('local');
      }
    }
    writeLocalThemes(list);
  }

  async function onSave() {
    setBusy(true);
    setError(null);
    try {
      await patchMySettings(patchOf(draft));
      setSaved(draft);
      setSavedAt(Date.now());
    } catch (err) {
      setError(tSettings('settings.theme.err_save', {
        msg: err instanceof Error ? err.message : String(err),
      }));
    } finally {
      setBusy(false);
    }
  }

  function onReset() {
    setOpenKey(null);
    preview({ ...EMPTY, transitionMs: draft.transitionMs });
  }

  /**
   * 기본 제공 테마 불러오기 — 테마 설정의 카드와 **같은 결과**가 나오게 한다(2026-08-20 사용자 확정).
   *
   * 종전에는 색 3개만 얹어서, 같은 이름·같은 색 띠인데 테마 설정에서 고를 때와 결과가 달랐다
   * (거기서는 레일·상태바·보드까지 물든다). 이제 여기서도 영역 색을 함께 채운다.
   *
   * ★ 다만 **모드는 바꾸지 않는다** — 라이트 화면에서 다크 테마를 불러왔다고 화면이 통째로
   *   어두워지면 "출발점을 고른다" 가 아니라 "테마를 갈아탄다" 가 된다. 그래서 팔레트는
   *   카드의 모드가 아니라 **지금 화면의 모드**로 만든다(라이트면 옅게, 다크면 짙게).
   */
  function loadBuiltIn(p: BuiltInTheme) {
    setOpenKey(null);
    const mode = document.documentElement.dataset.mode === 'dark' ? 'dark' : 'light';
    const pal = paletteFor({
      id: p.id, nameKey: p.nameKey, mode, primary: p.primary, accent: p.accent, danger: p.danger,
    });
    preview({
      ...draft,
      primary: p.primary, accent: p.accent, danger: p.danger,
      line: pal.line, bg: pal.bg, rail: pal.rail, menu: pal.menu, rrail: pal.rrail,
      status: pal.status, table: pal.table, tableHead: pal.tableHead,
      tableHeadText: pal.tableHeadText, icon: pal.icon, iconHover: pal.iconHover,
      tint: pal.tint, text: pal.text, textMuted: pal.textMuted,
    });
  }

  /** 내 테마 불러오기 — 등록할 때의 화면을 그대로 되살린다(영역 색·모서리·속도 포함). */
  function loadSaved(t: SavedTheme) {
    setOpenKey(null);
    const next: Draft = {
      ...EMPTY,
      accent: t.accent, danger: t.danger, surface: t.surface,
      buttonRadius: t.buttonRadius, transitionMs: t.transitionMs,
    };
    for (const f of FIELDS) next[f.key] = t[f.key] ?? null;
    preview(next);
  }

  async function onRegister() {
    if (themes.length >= MAX_SAVED_THEMES) {
      setError(tSettings('settings.theme.full', { n: MAX_SAVED_THEMES }));
      return;
    }
    setBusy(true);
    setError(null);
    const name = uniqueName(nameInput.trim() || tSettings('settings.theme.name_default'), themes);
    const entry = makeSavedTheme(name, draft, new Date().toISOString());
    try {
      await persistThemes([...themes, entry]);
      setNaming(false);
      setNameInput('');
    } finally {
      setBusy(false);
    }
    // 등록은 "이 색으로 하겠다" 는 뜻이기도 하다 — 목록에만 넣으면 두 번 눌러야 한다.
    if (dirty) await onSave();
  }

  async function onDelete(id: string) {
    await persistThemes(themes.filter((t) => t.id !== id));
  }

  return (
    <>
      {/* 미리보기와 색 고르기를 **한 상자**에 둔다 — 카드를 둘로 나누면 그 사이 여백만으로
          한 화면이 넘어가, 정작 고른 내역을 보려고 스크롤하게 된다(2026-08-20 사용자 요청). */}
      <section className="settings-section tm-panel" aria-label={tSettings('settings.theme.heading')}>
        <h2>{tSettings('settings.theme.preview')}</h2>
        <div className="tm-preview__grid">
          <div className="tm-preview__txt">
            <p>{tSettings('settings.theme.preview_desc')}</p>
            <p className="tm-legend">
              <span><b>①</b> {tSettings('settings.theme.lg_rail')}</span>
              <span><b>②</b> {tSettings('settings.theme.lg_menu')}</span>
              <span><b>③</b> {tSettings('settings.theme.lg_board')}</span>
              <span><b>④</b> {tSettings('settings.theme.lg_table')}</span>
              <span><b>⑤</b> {tSettings('settings.theme.lg_rrail')}</span>
              <span><b>⑥</b> {tSettings('settings.theme.lg_status')}</span>
            </p>
          </div>
          <ThemePreview c={previewColors} />
        </div>

        <h2 className="tm-h2">{tSettings('settings.theme.heading')}</h2>

        <span className="tm-group-lab">
          {tSettings('settings.theme.load')}<em>{tSettings('settings.theme.builtin')}</em>
        </span>
        <div className="tm-cards" role="group" aria-label={tSettings('settings.theme.builtin')}>
          {BUILT_IN_THEMES.map((p) => (
            <button
              key={p.id}
              type="button"
              className={`tm-card${sameAsBuiltIn(p, draft) ? ' is-on' : ''}`}
              aria-pressed={sameAsBuiltIn(p, draft)}
              onClick={() => loadBuiltIn(p)}
            >
              <span className="tm-card__strip" aria-hidden>
                <i style={{ background: p.primary }} />
                <i style={{ background: p.accent }} />
                <i style={{ background: p.danger }} />
              </span>
              <span className="tm-card__name">{tSettings(p.nameKey)}</span>
            </button>
          ))}
        </div>

        <span className="tm-group-lab">
          {tSettings('settings.theme.mine')}
          {themes.length > 0 && <em>{themes.length}/{MAX_SAVED_THEMES}</em>}
        </span>
        {themes.length === 0 ? (
          <p className="tm-empty">{tSettings('settings.theme.mine_empty')}</p>
        ) : (
          <div className="tm-cards" role="group" aria-label={tSettings('settings.theme.mine')}>
            {themes.map((t) => (
              /* 카드 안에 버튼을 또 넣을 수 없다(button 중첩 금지) — 형제로 두고 겹쳐 놓는다. */
              <div key={t.id} className={`tm-card-wrap${sameAsDraft(t, draft) ? ' is-on' : ''}`}>
                <button
                  type="button"
                  className={`tm-card${sameAsDraft(t, draft) ? ' is-on' : ''}`}
                  aria-pressed={sameAsDraft(t, draft)}
                  onClick={() => loadSaved(t)}
                >
                  <span className="tm-card__strip" aria-hidden>
                    <i style={{ background: t.primary ?? 'var(--primary)' }} />
                    <i style={{ background: t.rail ?? t.accent ?? 'var(--surface)' }} />
                    <i style={{ background: t.status ?? t.danger ?? 'var(--surface-2)' }} />
                  </span>
                  <span className="tm-card__name">{t.name}</span>
                </button>
                <button
                  type="button"
                  className="tm-card__del"
                  aria-label={`${t.name} — ${tSettings('settings.theme.delete_one')}`}
                  onClick={() => { void onDelete(t.id); }}
                >
                  <X size={12} aria-hidden />
                </button>
              </div>
            ))}
          </div>
        )}
        {store === 'local' && <p className="tm-note">{tSettings('settings.theme.local_only')}</p>}

        {/* ── 색 15종 — 설정된 색 한 칸 + 플로팅 색상값 + 커스텀 ── */}
        <div className="tm-colorgrid">
          {FIELDS.map((f) => {
            const shown = previewColors[PREVIEW_OF[f.key]];
            const isOpen = openKey === f.key;
            return (
              <div className="tm-crow" key={f.key}>
                <span className="tm-crow__lab" title={tSettings(f.descKey)}>
                  <b>{tSettings(f.labelKey)}</b>
                </span>

                <button
                  type="button"
                  className="tm-chip"
                  style={{ background: shown }}
                  aria-haspopup="dialog"
                  aria-expanded={isOpen}
                  aria-label={tSettings('settings.theme.c_open', { name: tSettings(f.labelKey) })}
                  onClick={() => setOpenKey(isOpen ? null : f.key)}
                />

                <span className="tm-custom">
                  <input
                    type="color"
                    value={isHex(shown) ? shown : '#3b82f6'}
                    aria-label={tSettings('settings.theme.c_custom', { name: tSettings(f.labelKey) })}
                    onChange={(e) => preview({ ...draft, [f.key]: e.target.value })}
                  />
                  <span className="tm-custom__face" aria-hidden><Plus size={11} /></span>
                </span>

                {isOpen && (
                  <div className="tm-pop" role="dialog" aria-label={tSettings(f.labelKey)}>
                    <div className="tm-pop__val">
                      <span className="tm-pop__sw" style={{ background: shown }} />
                      <span className="tm-pop__hex">{shown.toUpperCase()}</span>
                      {draft[f.key] && (
                        <button
                          type="button"
                          className="tm-pop__clear"
                          onClick={() => preview({ ...draft, [f.key]: null })}
                        >
                          {tSettings('settings.theme.c_default')}
                        </button>
                      )}
                    </div>
                    <p className="tm-pop__lab">{tSettings('settings.theme.c_theme_colors')}</p>
                    <div className="tm-pop__list">
                      {themeColors.map((hex) => (
                        <button
                          key={hex}
                          type="button"
                          style={{ background: hex }}
                          aria-label={hex}
                          aria-pressed={hex === (draft[f.key] ?? '').toLowerCase()}
                          onClick={() => preview({ ...draft, [f.key]: hex })}
                        />
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* 버튼 모서리 · 바뀌는 속도 — 둘 다 선택지가 짧아 한 줄에 둔다. */}
        <div className="tm-row tm-row--pair">
          <div className="tm-pair__cell">
            <span className="tm-row__label">
              <b>{tSettings('settings.theme.radius')}</b>
              <span>{tSettings('settings.theme.radius_desc')}</span>
            </span>
            <div className="st-optrow" role="radiogroup" aria-label={tSettings('settings.theme.radius')}>
              {RADII.map(({ key, labelKey }) => (
                <button
                  key={key}
                  type="button"
                  role="radio"
                  aria-checked={draft.buttonRadius === key}
                  className={`st-opt${draft.buttonRadius === key ? ' is-on' : ''}`}
                  onClick={() => preview({ ...draft, buttonRadius: key })}
                >
                  {tSettings(labelKey)}
                </button>
              ))}
            </div>
          </div>

          <div className="tm-pair__cell">
            <span className="tm-row__label">
              <b>{tSettings('settings.theme.speed')}</b>
              <span>{tSettings('settings.theme.speed_desc')}</span>
            </span>
            <div className="st-optrow" role="radiogroup" aria-label={tSettings('settings.theme.speed')}>
              {SPEEDS.map(({ ms, labelKey }) => (
                <button
                  key={ms}
                  type="button"
                  role="radio"
                  aria-checked={draft.transitionMs === ms}
                  className={`st-opt${draft.transitionMs === ms ? ' is-on' : ''}`}
                  onClick={() => preview({ ...draft, transitionMs: ms })}
                >
                  {tSettings(labelKey)}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="tm-actions">
          <button type="button" className="settings-btn" disabled={busy || !dirty} onClick={onSave}>
            {busy ? <Loader2 size={14} className="tm-spin" aria-hidden /> : null}
            {tSettings(busy ? 'settings.btn.saving' : 'settings.btn.save')}
          </button>

          {naming ? (
            <span className="tm-name">
              <input
                type="text"
                className="tm-name__input"
                value={nameInput}
                maxLength={THEME_NAME_MAX}
                autoFocus
                placeholder={tSettings('settings.theme.name_ph')}
                aria-label={tSettings('settings.theme.name_ph')}
                onChange={(e) => setNameInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') void onRegister(); }}
              />
              <button type="button" className="settings-btn" disabled={busy} onClick={() => { void onRegister(); }}>
                {tSettings('settings.theme.register_confirm')}
              </button>
              <button type="button" className="st-opt" onClick={() => { setNaming(false); setNameInput(''); }}>
                {tSettings('settings.theme.cancel')}
              </button>
            </span>
          ) : (
            <button type="button" className="st-opt" onClick={() => setNaming(true)}>
              <Plus size={13} aria-hidden />{tSettings('settings.theme.register')}
            </button>
          )}

          <button type="button" className="st-opt" onClick={onReset}>
            <RotateCcw size={13} aria-hidden />{tSettings('settings.theme.reset')}
          </button>
          {savedAt && !error && (
            <span className="settings-success"><Check size={14} aria-hidden /> {tSettings('settings.msg.saved')}</span>
          )}
          {error && <span className="settings-error" role="alert">{error}</span>}
        </div>
      </section>
    </>
  );
}
